import { formatIDR } from '@/lib/utils';
import { StoreProduct } from '@/types/http';

type PriceItem = {
  item_id?: string | null;
  item_name?: string | null;
  sell_price?: number | null;
  discount_price?: number | null;
};

type PricebookItem = {
  item_id?: string | null;
  min_unit?: number | null;
  retail_price?: number | null;
};

type VariantId = string | null;

type PriceView = { topText: string; bottomText: string | null; hasPricebook: boolean };

/**
 * Converts the given value to a number, returning `0` if the value is `null`, `undefined`, or `NaN`.
 *
 * @param n - The value to convert, which may be a number, `null`, or `undefined`.
 * @returns The numeric value if valid, otherwise `0`.
 */
export const toNum = (n?: number | null) => (typeof n === 'number' && !Number.isNaN(n) ? n : 0);

/**
 * Checks if the provided value is a valid non-empty string ID.
 *
 * @param id - The value to validate as an ID.
 * @returns `true` if `id` is a non-empty string, otherwise `false`.
 */
export const isValidId = (id: unknown): id is string =>
  typeof id === 'string' && id.trim().length > 0;

const effPrice = (sell: number, disc: number) => (disc > 0 && disc < sell ? disc : sell);
const formatRange = (min: number, max: number) =>
  min <= 0 ? '' : min === max ? formatIDR(min) : `${formatIDR(min)} - ${formatIDR(max)}`;
const withNameIf = (label: string, allow: boolean, unique: boolean, name?: string) =>
  allow && unique && name ? `${label}` : label;

/**
 * Computes a formatted price view for a given product, supporting both range and single price modes.
 *
 * This function normalizes product variants, aggregates price and discount information,
 * and determines the best price to display based on product data and pricebook entries.
 * It handles various scenarios including multiple variants, discounts, and pricebook overrides.
 *
 * @param product - The product object, which may include price ranges and pricebook entries.
 * @param opts - Options for price view computation.
 * @param opts.mode - Determines the computation mode: `'range'` for aggregated price ranges, `'single'` for the best single price.
 * @returns An object containing formatted price information for display, including top and bottom text and pricebook status.
 *
 * @remarks
 * - In `'range'` mode, the function aggregates prices and discounts across variants.
 * - In `'single'` mode, it selects the best effective price, considering pricebook overrides.
 * - The returned object includes formatted price strings and flags for pricebook usage.
 */
export function computePriceView(
  product: StoreProduct & { price_range?: PriceItem[]; pricebook?: PricebookItem[] },
  opts: { mode: 'range' | 'single' } = { mode: 'range' }
): PriceView {
  // ---------- Normalize variants ----------
  const rawItems: PriceItem[] = product.price_range?.length
    ? product.price_range
    : [{ item_name: null, sell_price: product.sell_price, discount_price: product.discount_price }];

  const allowName = Boolean(product.has_variation ?? false);

  const variants = rawItems.map((i) => {
    const sell = toNum(i.sell_price);
    const disc = toNum(i.discount_price);
    return {
      id: (i.item_id ?? '') as VariantId,
      name: i.item_name ?? '',
      sell,
      disc,
      eff: effPrice(sell, disc),
      hasDisc: disc > 0 && disc < sell,
    };
  });

  // Early out: no variant price at all
  if (!variants.length) {
    return { topText: formatIDR(0), bottomText: null, hasPricebook: false };
  }

  // ---------- Prepare pricebook maps ----------
  const pbHasId = new Set<string>();
  const pbBestMin1 = new Map<string, number>(); // min_unit === 1 → best price

  for (const pb of product.pricebook ?? []) {
    const id = pb.item_id ?? null;
    if (isValidId(id)) pbHasId.add(id);

    const key = String(pb.item_id ?? '');
    const price = toNum(pb.retail_price);
    const minUnit = toNum(pb.min_unit);
    if (price > 0 && minUnit === 1) {
      const cur = pbBestMin1.get(key);
      if (cur == null || price < cur) pbBestMin1.set(key, price);
    }
  }

  const hasPBById = (variantId: VariantId) => isValidId(variantId) && pbHasId.has(variantId);

  // =========================================================
  // MODE: RANGE
  // =========================================================
  if (opts.mode === 'range') {
    // Single pass aggregations
    let sellMin = Infinity,
      sellMax = 0;
    let discMin = Infinity,
      discMax = 0,
      discCount = 0;

    for (const v of variants) {
      if (v.sell > 0) {
        if (v.sell < sellMin) sellMin = v.sell;
        if (v.sell > sellMax) sellMax = v.sell;
      }
      if (v.hasDisc) {
        discCount++;
        if (v.disc < discMin) discMin = v.disc;
        if (v.disc > discMax) discMax = v.disc;
      }
    }

    const hasSell = Number.isFinite(sellMin);
    const hasDisc = discCount > 0;

    // CASE A: multi-variant & hanya 1 diskon → tampilkan diskon unik + (nama) jika unik by eff
    if (variants.length > 1 && discCount === 1) {
      const v = variants.find((x) => x.hasDisc)!;

      // cek apakah nilai eff (yang sama dengan disc) itu unik
      let sameEff = 0;
      for (const x of variants) if (effPrice(x.sell, x.disc) === v.disc) sameEff++;
      const isUnique = sameEff === 1;

      if (v.disc === v.sell) {
        return { topText: formatIDR(v.sell), bottomText: null, hasPricebook: hasPBById(v.id) };
      }
      return {
        topText: withNameIf(formatIDR(v.disc), allowName, isUnique, v.name),
        bottomText: v.disc === v.sell ? null : formatIDR(v.sell),
        hasPricebook: hasPBById(v.id),
      };
    }

    // CASE B: ada beberapa diskon
    if (hasDisc) {
      // Kondisi khusus: sell homogen & disc homogen & disc == sell
      if (
        hasSell &&
        sellMin === sellMax &&
        discMin === discMax &&
        discMin === sellMin &&
        Number.isFinite(discMin)
      ) {
        const chosen = variants.find((x) => effPrice(x.sell, x.disc) === discMin);
        // cek keunikan eff discMin
        let sameEff = 0;
        for (const x of variants) if (effPrice(x.sell, x.disc) === discMin) sameEff++;
        const isUnique = sameEff === 1;

        return {
          topText: withNameIf(formatIDR(discMin), allowName, isUnique, chosen?.name),
          bottomText: null,
          hasPricebook: chosen ? hasPBById(chosen.id) : false,
        };
      }

      const discLabel = Number.isFinite(discMin) ? formatRange(discMin, discMax) : '';
      const sellLabel = hasSell ? formatRange(sellMin, sellMax) : null;

      return { topText: discLabel, bottomText: sellLabel, hasPricebook: false };
    }

    // CASE C: tidak ada diskon → tampilkan sell (range/single)
    if (hasSell && sellMin === sellMax) {
      // cari varian yang match sellMin untuk cek (nama) & hasPB
      let chosen: (typeof variants)[number] | undefined;
      let countSameSell = 0;
      for (const v of variants) {
        if (v.sell === sellMin) {
          if (!chosen) chosen = v;
          countSameSell++;
        }
      }
      const isUnique = countSameSell === 1;

      return {
        topText: withNameIf(formatIDR(sellMin), allowName, isUnique, chosen?.name),
        bottomText: null,
        hasPricebook: chosen ? hasPBById(chosen.id) : false,
      };
    }

    return {
      topText: hasSell ? formatRange(sellMin, sellMax) : formatIDR(0),
      bottomText: null,
      hasPricebook: false,
    };
  }

  // =========================================================
  // MODE: SINGLE
  // =========================================================

  // price_range (eff) → min & winners
  let prMin = Infinity;
  let prWinnersIdx: number[] = [];
  for (let i = 0; i < variants.length; i++) {
    const v = variants[i];
    const e = v.eff;
    if (e > 0) {
      if (e < prMin) {
        prMin = e;
        prWinnersIdx = [i];
      } else if (e === prMin) {
        prWinnersIdx.push(i);
      }
    }
  }

  // pricebook (min_unit=1) → min & winners
  let pbMin = Infinity;
  let pbWinnersIdx: number[] = [];
  for (let i = 0; i < variants.length; i++) {
    const v = variants[i];
    const key = String(v.id ?? '');
    const best = pbBestMin1.get(key);
    if (best == null || best <= 0) continue;
    if (best < pbMin) {
      pbMin = best;
      pbWinnersIdx = [i];
    } else if (best === pbMin) {
      pbWinnersIdx.push(i);
    }
  }

  if (!Number.isFinite(prMin) && !Number.isFinite(pbMin)) {
    return {
      topText: formatIDR(0),
      bottomText: null,
      hasPricebook: false,
    };
  }

  const pricebookWins = Number.isFinite(pbMin) && pbMin < prMin;

  if (pricebookWins) {
    const idx = pbWinnersIdx[0];
    const chosen = variants[idx];

    // Jika semua winner PB punya sell sama & sell > pbMin → tampilkan strike sell
    let sameSell = true;
    const refSell = variants[pbWinnersIdx[0]].sell;
    for (const j of pbWinnersIdx)
      if (variants[j].sell !== refSell) {
        sameSell = false;
        break;
      }
    const strike = sameSell && refSell > pbMin ? formatIDR(refSell) : null;

    return {
      topText: withNameIf(formatIDR(pbMin), allowName, pbWinnersIdx.length === 1, chosen?.name),
      bottomText: strike,
      hasPricebook: hasPBById(chosen?.id ?? null),
    };
  }

  if (prWinnersIdx.length === 0) {
    return {
      topText: formatIDR(0),
      bottomText: null,
      hasPricebook: false,
    };
  }

  // price_range menang (tie → prefer price_range)
  const chosen = variants[prWinnersIdx[0]];
  const winners = prWinnersIdx.map((i) => variants[i]);
  const hasDisc = chosen?.hasDisc;

  if (prWinnersIdx.length === 1) {
    const topVal = hasDisc ? chosen?.disc : prMin;
    return {
      topText: withNameIf(formatIDR(topVal), allowName, true, chosen?.name),
      bottomText: hasDisc ? formatIDR(chosen?.sell) : null,
      hasPricebook: hasPBById(chosen?.id),
    };
  }

  // Multiple winners pada eff yang sama
  const allDisc = winners.every((w) => w.hasDisc);
  let sameSell = true;
  const refSell = winners[0]?.sell;
  for (let k = 1; k < winners.length; k++)
    if (winners[k].sell !== refSell) {
      sameSell = false;
      break;
    }
  const strike = allDisc && sameSell && refSell > prMin ? formatIDR(refSell) : null;

  return {
    topText: formatIDR(prMin),
    bottomText: strike,
    hasPricebook: hasPBById(chosen?.id ?? null),
  };
}
